import { type RefObject, useEffect, useState } from 'react';

import type { LensCard } from '~/types/leLens';

import { autoTableRowSpan, measureTableHeight, tableUsesAutoHeight } from './tableSizing';

export default function useAutoTableHeights(
  cards: ReadonlyArray<LensCard>,
  rootRef: RefObject<HTMLDivElement | null>,
  width: number,
  interacting: boolean,
): Record<string, number> {
  const [heights, setHeights] = useState<Record<string, number>>({});
  // Callers can construct display cards each render. Observe only when the
  // eligible ids change; row and text changes are caught by the DOM observers.
  const ids = JSON.stringify(cards.filter(tableUsesAutoHeight).map((card) => card.id));
  useEffect(() => {
    const root = rootRef.current;
    if (!root || width <= 0 || interacting || typeof ResizeObserver === 'undefined') return;
    const eligible = new Set<string>(JSON.parse(ids));
    let pending = 0;
    const measure = () => {
      pending = 0;
      const next: Record<string, number> = {};
      root.querySelectorAll<HTMLElement>('[data-lens-card-frame]').forEach((frame) => {
        const id = frame.dataset.lensCardFrame;
        if (!id || !eligible.has(id)) return;
        const height = measureTableHeight(frame);
        if (height !== undefined) next[id] = autoTableRowSpan(height);
      });
      setHeights((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    };
    const schedule = () => {
      if (!pending) pending = requestAnimationFrame(measure);
    };
    const resize = new ResizeObserver(schedule);
    const observe = () => {
      resize.disconnect();
      resize.observe(root);
      root
        .querySelectorAll('[data-lens-table-content], [data-lens-table-notice], [data-lens-card-body], .MuiCard-root')
        .forEach((node) => resize.observe(node));
      schedule();
    };
    const changes = new MutationObserver(observe);
    changes.observe(root, { childList: true, characterData: true, subtree: true });
    observe();
    return () => {
      resize.disconnect();
      changes.disconnect();
      if (pending) cancelAnimationFrame(pending);
    };
  }, [ids, rootRef, width, interacting]);
  return heights;
}
